<script lang="ts" setup>
import {
  onMounted,
  ref,
  useRoute,
  computed,
  useRouter,
  useMetrics,
  nextTick,
  onBeforeUnmount,
  useAuth,
  useDialog,
  useCreators,
  useTemplateRef,
  callOnce,
  useUser,
  useFollowers,
  useHeaderStyle,
  useBookmarks,
} from "#imports";
import MPAuth from "~/components/dialogs/MPAuth/MPAuth.vue";
import MPMap from "~/components/layouts/MPMap.client.vue";
import MPMenu from "~/components/layouts/MPMenu.vue";
import MPImg from "~/components/widgets/MPImg.vue";

const authStore = useAuth();
const followStore = useFollowers();
const creatorStore = useCreators();
const userStore = useUser();
const bookmarkStore = useBookmarks();

const { openDialog, closeDialog } = useDialog();
const { trackEvent } = useMetrics();
useHeaderStyle("opaque");

const openAuthScreen = () => {
  openDialog(MPAuth, { startOn: "signup" });
};

const isMounted = ref(false); // wait till this component is mounted to ensure the teleport to #page-filter works
onMounted(() => {
  isMounted.value = true;
  nextTick(() => {
    if (!authStore.authUser) {
      trackEvent("joined", {
        friction: "join_prompt_on_atlas",
      });
      openAuthScreen();
      return;
    }
  });
});

onBeforeUnmount(() => {
  closeDialog();
});

const data = computed(() => {
  const followerIds = followStore.followers?.map(
    (follower) => follower.creator_id,
  );
  const followers = creatorStore.getByIds(followerIds ?? []).value!;
  const supportsIds = userStore.user.supports.map(
    (supports) => supports.creator_id as string,
  );
  const supporters = creatorStore.getByIds(supportsIds ?? []).value!;

  const spotBookmarkIds = bookmarkStore.spotBookmarks.map(
    (bookmark) => bookmark.spot_id,
  );

  return {
    followers,
    supporters,
    spotBookmarkIds,
  };
});

const mapComponent = useTemplateRef("mapComponent");
const scrollingContainer = useTemplateRef("scrollingContainer");

const handleSelectSpot = (spotId: string | null) => {
  if (mapComponent.value) {
    if (spotId == null) {
      mapComponent.value.fitSpotBounds();
    } else {
      mapComponent.value.selectSpot(spotId);
    }
  }
};

const loadData = async () => {
  await followStore.fetchFollowers();
  await bookmarkStore.fetchSpotBookmarks();
  await bookmarkStore.fetchSpotBookmarks();

  const followerIds = followStore.followers.map(
    (follower) => follower.creator_id,
  );

  const supportsIds = userStore.user.supports.map(
    (supports) => supports.creator_id as string,
  );
  await creatorStore.fetchByIds(followerIds.concat(supportsIds));
};

await callOnce(loadData, { mode: "navigation" });

const router = useRouter();
const route = useRoute();
const menuItems = computed<InstanceType<typeof MPMenu>["$props"]["menuItems"]>(
  () => [
    {
      label: "My Atlas",
      id: "my-atlas",
      showLabel: false,
      withDivider: true,
      items: [
        {
          label: "Saved Spots",
          icon: "ri-map-pin-fill",
          isActive:
            route.path === "/atlas" || route.path.startsWith("/atlas/spots"),
          action: () => router.push("/atlas"),
        },
        {
          label: "Saved Maps",
          icon: "ri-map-2-fill",
          isActive: route.path.startsWith("/atlas/maps"),
          action: () => router.push("/atlas/maps"),
        },
      ],
    },
    {
      label: "Supporting",
      id: "supporting",
      showLabel: true,
      withDivider: false,
      withSlot: true,
    },
    {
      label: "Following",
      id: "following",
      showLabel: true,
      withDivider: false,
      withSlot: true,
    },
  ],
);

const openSpot = async (spotId: string) => {
  await router.push(`/atlas/spots/${spotId}`);
  mapComponent.value!.selectSpot(spotId);
};
</script>

<template>
  <div class="atlas">
    <div id="nav">
      <aside class="desktop">
        <h2>My Atlas</h2>
        <p>Manage your saved spots, saved maps, and creators your following.</p>
        <MPMenu :menuItems="menuItems">
          <template #supporting>
            <span v-if="data.supporters.length === 0"
              >You are not currently supporting any creators</span
            >
            <ul v-else class="supporting-list">
              <li v-for="creator in data.supporters" :key="creator.id">
                <NuxtLinkLocale :to="`/@${creator.mappr_handle}`">
                  <MPImg
                    :src="
                      creator.profile_image_url ||
                      '/media/images/placeholder-user.webp'
                    "
                    :width="35"
                    :height="35"
                    :style="{ borderRadius: '100%' }"
                  />
                  @{{ creator.mappr_handle }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </template>
          <template #following>
            <span v-if="data.followers && data.followers.length === 0"
              >You are not following any creators</span
            >
            <ul v-else class="following-list">
              <li v-for="creator in data.followers" :key="creator.id">
                <NuxtLinkLocale :to="`/@${creator.mappr_handle}`">
                  <MPImg
                    :src="
                      creator.profile_image_url ||
                      '/media/images/placeholder-user.webp'
                    "
                    :width="35"
                    :height="35"
                    :style="{ borderRadius: '100%' }"
                  />
                  @{{ creator.mappr_handle }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </template>
        </MPMenu>
      </aside>
      <aside class="mobile">
        <ul>
          <li v-for="item in menuItems[0]!.items" :key="item.label">
            <a
              :label="item.label"
              :class="[{ active: item.isActive }]"
              @click="item.action"
            >
              <i :class="item.icon" />
              {{ item.label }}
            </a>
          </li>
        </ul>
      </aside>
    </div>
    <div id="map">
      <MPMap
        ref="mapComponent"
        :spotIds="data.spotBookmarkIds"
        @spotClick="openSpot($event)"
      />
    </div>
    <div id="page-filters" />
    <div id="list" ref="scrollingContainer">
      <hr class="pull-handle" />
      <NuxtPage
        v-if="isMounted"
        @setSpot="handleSelectSpot($event)"
        :scrollingContainer="scrollingContainer"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "assets/css/breakpoints.pcss";
@import "assets/css/scrollbar.pcss";
@import "assets/css/layouts.pcss";

/* base grid layout */
.atlas {
  display: grid;
  width: 100vw;
  min-height: calc(100vh - 4rem);
  position: relative;
  left: 0;
  grid-template-areas:
    "map"
    "list";
  grid-template-rows: 80vh 1fr;
  @mixin screen md {
    position: relative;
    min-height: auto;
    height: 100dvh;
    grid-template-areas:
      "nav filters map"
      "nav list map";
    grid-template-columns: 18rem 25rem 1fr;
    grid-template-rows: 0 100%;
  }
}

#map {
  grid-area: map;
}

#page-filters {
  z-index: 1000;
}

#nav {
  grid-area: nav;
  z-index: 1000;
  @mixin screen md {
    margin-top: 3rem;
  }
  aside.desktop {
    display: none;
    @mixin screen md {
      display: block;
    }
  }
  aside.mobile {
    display: flex;
    @mixin screen md {
      display: none;
    }
  }
}

#list {
  grid-area: list;
  background: white;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  @mixin screen md {
    box-shadow: none;
  }
}

/*======
Map
======*/
#map {
  background-color: #cbdcf2;
  position: fixed;
  width: 100%;
  height: 80vh;
  @mixin screen md {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100%;
  }
}
/*======
Filters
======*/
#page-filters {
  position: fixed;
  top: 4.75rem;
  right: 1rem;
  height: fit-content;
  width: calc(100% - 2rem);
  z-index: 0;
  @mixin screen md {
    width: 100%;
    position: relative;
    max-width: auto;
    top: 0;
    left: 0;
    background-color: white;
  }
}

/*======
Nav
======*/
#nav {
  overflow-x: scroll;
  position: fixed;
  top: 0rem;
  left: 0;
  padding-bottom: 1rem;
  width: 100%;
  z-index: 1;
  @mixin scrollbar-reset;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  @mixin screen md {
    background-color: var(--color-slate-100);
    position: relative;
    top: 0;
    padding-left: 2vw;
    padding-right: 1rem;
  }
}

/* #nav aside.mobile ul {
  padding: 0.5rem 1rem 0.5rem;
  margin: 0;
  list-style: none;
  display: flex;
  gap: 0.75rem;
  overflow-y: scroll;
  @mixin scrollbar-reset;
} */

#nav aside.mobile ul {
  list-style: none;
  padding: 0 0 0 3.75rem;
  margin: 0;
  display: inline-flex;
  overflow-x: scroll;
  @mixin scrollbar-reset;
  @mixin screen md {
    padding: 1rem 0 0;
  }
}

#nav aside.mobile ul li a {
  display: block;
  padding: 1.15rem 0.65rem;
  text-decoration: none;
  color: var(--color-slate-500);
  transition: color 0.2s ease-in-out;
  @mixin screen md {
    padding: 0 1rem;
  }
}

#nav aside.mobile ul li a i {
  font-size: 1.25rem;
}

#nav aside.mobile ul li a.active {
  color: var(--color-slate-700);
  font-weight: 500;
  transition: color 0.2s ease-in-out;
}

#nav aside.desktop h2 {
  font-size: 1.2rem;
  font-weight: medium;
  color: var(--color-slate-600);
  margin: 2rem 0 0.5rem;
}

#nav aside.desktop p {
  font-size: 0.9rem;
  margin: 0.5rem 0 1.25rem;
  color: var(--color-slate-500);
}

#nav aside.desktop span {
  display: flex;
  line-height: 1.2;
  font-size: 0.9rem;
  margin: 0.5rem 0.5rem 1.25rem;
  color: var(--color-slate-500);
}

/*======
List
======*/

.pull-handle {
  display: block;
  @mixin screen md {
    display: none;
  }
}
#list {
  overflow-y: scroll;
  /* border-radius: 1.5rem; */
  position: relative;
  top: -3rem;
  padding-top: 1rem;
  z-index: 0;
  @mixin scrollbar-reset;
  &:deep(> div) {
    max-width: 40rem;
    margin: 1rem auto;
  }
  @mixin screen md {
    padding-top: 0;
    top: 0;
  }
}

.join-message-wrapper {
  position: fixed;
  left: 0;
  width: 100%;
  z-index: 9000;
  height: calc(100dvh - 4rem);
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(1px);
  padding-top: 3rem;
  @mixin screen md {
    padding: 5rem 0;
    top: 3.25rem;
    height: calc(100dvh - 3.25rem);
  }
}

.supporting-list {
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    img {
      height: 35px;
      width: 35px;
    }
  }
  li a {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.following-list {
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    img {
      height: 35px;
      width: 35px;
    }
  }
  li a {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
